import { Suspense, lazy, type ReactNode } from "react";
import { Switch, Route, Redirect } from "wouter";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { AuthProvider, useAuth, type UserRole } from "@/hooks/useAuth";
import AuthGateFallback from "@/components/AuthGateFallback";
import PageFallback from "@/components/PageFallback";
import NotFound from "@/pages/not-found";
import HomePage from "@/pages/HomePage";

const ManagePage = lazy(() => import("@/pages/ManagePage"));
const PrintDeliveryNote = lazy(() => import("@/pages/PrintDeliveryNote"));
const PrintOrderStats = lazy(() => import("@/pages/PrintOrderStats"));
const DriverDashboard = lazy(() => import("@/pages/DriverDashboard"));
const SignaturePad = lazy(() => import("@/pages/SignaturePad"));

// FE-02: 受保護路由守衛 — 未登入或角色不符時重導至首頁
function RequireAuth({ children, roles }: { children: ReactNode; roles?: UserRole[] }) {
  const { isAuthenticated, isReady, hasRole } = useAuth();

  if (!isReady) return <AuthGateFallback />;
  if (!isAuthenticated) return <Redirect to="/" />;
  if (roles && roles.length > 0 && !hasRole(...roles)) return <Redirect to="/" />;

  return <>{children}</>;
}

function Router() {
  return (
    <Suspense fallback={<PageFallback />}>
      <Switch>
        <Route path="/">{() => <ErrorBoundary><HomePage /></ErrorBoundary>}</Route>

        {/* FE-02: /manage 開放 admin/staff；網域與分頁可見性由 DomainNav/manageSections 依角色過濾 */}
        <Route path="/manage/:section?">{() =>
          <RequireAuth roles={["admin", "staff"]}>
            <ErrorBoundary><ManagePage /></ErrorBoundary>
          </RequireAuth>
        }</Route>

        {/* FE-02: /driver 需要登入（admin/staff/driver 均可） */}
        <Route path="/driver">{() =>
          <RequireAuth>
            <ErrorBoundary><DriverDashboard /></ErrorBoundary>
          </RequireAuth>
        }</Route>

        {/* /sign: 需要登入（admin 或 driver 可掃碼簽收），與後端 requireRole("admin","driver") 一致 */}
        <Route path="/sign/:orderId">{() =>
          <RequireAuth roles={["admin", "driver"]}>
            <ErrorBoundary><SignaturePad /></ErrorBoundary>
          </RequireAuth>
        }</Route>

        {/* /print/stats: 訂單統計列印（僅 admin）；須註冊於 /print/:orderId 之前 */}
        <Route path="/print/stats">{() =>
          <RequireAuth roles={["admin"]}>
            <ErrorBoundary><PrintOrderStats /></ErrorBoundary>
          </RequireAuth>
        }</Route>

        {/* /print: 需要 admin 或 staff，與後端 requireRole("admin","staff") 一致 */}
        <Route path="/print/group/:groupId">{() =>
          <RequireAuth roles={["admin", "staff"]}>
            <ErrorBoundary><PrintDeliveryNote /></ErrorBoundary>
          </RequireAuth>
        }</Route>
        <Route path="/print/:orderId">{() =>
          <RequireAuth roles={["admin", "staff"]}>
            <ErrorBoundary><PrintDeliveryNote /></ErrorBoundary>
          </RequireAuth>
        }</Route>
        <Route>{() => <NotFound />}</Route>
      </Switch>
    </Suspense>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ErrorBoundary>
        <AuthProvider>
          <Router />
          <Toaster />
        </AuthProvider>
      </ErrorBoundary>
    </QueryClientProvider>
  );
}

export default App;
